import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, MessageCircle, Mail, X } from "lucide-react";
import { BRAND, CONTACT, IMAGES, LEGAL, waLink } from "@/config";

const LegalModal = ({ doc, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[80] flex items-center justify-center bg-navy/80 backdrop-blur-md p-5"
    onClick={onClose}
    data-testid="legal-modal-overlay"
  >
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 30, scale: 0.97 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-lg rounded-2xl border border-gold/30 bg-navy-800 p-8 sm:p-10"
      data-testid="legal-modal"
    >
      <div className="flex items-start justify-between gap-6">
        <h3 className="font-serif text-2xl text-cream">{doc.title}</h3>
        <button
          onClick={onClose}
          data-testid="legal-modal-close"
          className="text-mist hover:text-gold transition-colors p-1"
          aria-label="Kapat"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="mt-6 space-y-4">
        {doc.body.map((p, i) => (
          <p key={i} className="text-sm text-mist leading-relaxed">
            {p}
          </p>
        ))}
      </div>
    </motion.div>
  </motion.div>
);

const Footer = () => {
  const [modal, setModal] = useState(null);

  const navLinks = [
    { label: "Hakkımda", href: "#hakkimda" },
    { label: "Eğitimler", href: "#egitimler" },
    { label: "Danışmanlıklar", href: "#danismanlik" },
    { label: "Atölyeler", href: "#etkinlikler" },
    { label: "İletişim", href: "#iletisim" },
  ];

  return (
    <footer data-testid="site-footer" className="relative z-10 border-t border-gold/15 bg-navy-800/50">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <div className="inline-flex items-center rounded-xl bg-cream px-6 py-3 shadow-[0_0_30px_rgba(212,175,55,0.18)]">
              <img
                src={IMAGES.logoWordmark}
                alt="Neksus"
                data-testid="footer-logo"
                className="h-9 w-auto"
              />
            </div>
            <p className="mt-4 max-w-xs text-sm text-mist leading-relaxed">{BRAND.tagline}</p>
            <div className="mt-6 flex gap-3">
              <a
                href={CONTACT.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
              >
                <Instagram className="w-4.5 h-4.5" />
              </a>
              <a
                href={waLink("Merhaba, bilgi almak istiyorum.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                aria-label="WhatsApp"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
              >
                <MessageCircle className="w-4.5 h-4.5" />
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="footer-email"
                aria-label="E-posta"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
              >
                <Mail className="w-4.5 h-4.5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Site Haritası</h4>
            <nav className="mt-5 flex flex-col gap-3">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  className="w-fit link-underline text-sm text-mist hover:text-cream transition-colors"
                >
                  {l.label}
                </a>
              ))}
            </nav>
          </div>

          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Yasal & İletişim</h4>
            <div className="mt-5 flex flex-col gap-3">
              <button
                onClick={() => setModal("privacy")}
                data-testid="footer-privacy-link"
                className="w-fit link-underline text-left text-sm text-mist hover:text-cream transition-colors"
              >
                Gizlilik Politikası
              </button>
              <button
                onClick={() => setModal("terms")}
                data-testid="footer-terms-link"
                className="w-fit link-underline text-left text-sm text-mist hover:text-cream transition-colors"
              >
                Kullanım Şartları
              </button>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="footer-contact-email"
                className="w-fit link-underline text-sm text-mist hover:text-cream transition-colors"
              >
                {CONTACT.email}
              </a>
              <span className="text-sm text-mist">{CONTACT.whatsappDisplay}</span>
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-gold/10 pt-7 text-center">
          <p className="text-xs text-mist/70 tracking-wide">{BRAND.copyright}</p>
        </div>
      </div>

      <AnimatePresence>
        {modal && <LegalModal doc={LEGAL[modal]} onClose={() => setModal(null)} />}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;
